import React from "react";
import {
  ERROR_DOCUMENTO,
  validarDNI,
  validarDNIPersonaDirecto,
  validarCampoRequerido,
  validarFechas,
  validarValorPositivo,
  validarCantidadHuespedes,
} from "../../shared/utils/validaciones";
import { validarSeña } from "../../shared/utils/validaciones";
import { parseMonetaryValue } from "../../shared/utils/formatters";
import { apellidoObligatorio } from "../../clients/utils/clientIdentityHelpers";

interface FormData {
  entrada: Date | null;
  salida: Date | null;
  departamento: string;
  nombre: string;
  apellido: string;
  dni: string;
  valor_total: string;
  pago: string;
  ajuste_financiero: string;
  tipo_ajuste: string;
  cantidad_huespedes: string;
  tipo_cama: string[];
  localidad: string;
  email1: string;
  email2: string;
  telefono1: string;
  telefono2: string;
  acompanantes: Array<{
    nombre: string;
    apellido: string;
    dni: string;
    edad: string;
  }>;
  afiliado_nombre: string;
  afiliado_apellido: string;
  afiliado_dni: string;
  late_checkout: boolean;
  politica_precio?: string;
  precio_personalizado?: string;
  tipo_precio_personalizado?: string;
}

interface FormErrors {
  entrada?: string;
  salida?: string;
  departamento?: string;
  nombre?: string;
  apellido?: string;
  dni?: string;
  valor_total?: string;
  pago?: string;
  cantidad_huespedes?: string;
  email1?: string;
  email2?: string;
  telefono1?: string;
  telefono2?: string;
  edad?: string;
  overlap?: string;
}

export const validateFormForSubmit = (
  formData: FormData,
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>,
  overlapError: string | null,
  origenReserva?: string,
  esEmpresa?: boolean
): boolean => {
  // Si ya hay un error de superposición, no continuar con la validación
  if (overlapError) {
    setErrors({
      entrada: overlapError,
      salida: overlapError,
    });
    return false;
  }

  // Usar el valor del formulario en lugar de calcular dinámicamente
  const totalReserva = parseMonetaryValue(formData.valor_total);

  // Validar la seña (solo verificar que no sea negativa)
  const señaError = validarSeña(
    parseMonetaryValue(formData.pago),
    totalReserva
  );

  if (señaError) {
    setErrors({
      pago: señaError, // Establecer el mensaje de error para la seña
    });
    return false;
  }

  let formIsValid = true;
  const newErrors: FormErrors = {};

  // Validaciones de campos obligatorios y formato
  if (!formData.entrada) {
    newErrors.entrada = "La fecha de entrada es obligatoria";
    formIsValid = false;
  }
  if (!formData.salida) {
    newErrors.salida = "La fecha de salida es obligatoria";
    formIsValid = false;
  } else if (!validarFechas(formData.entrada, formData.salida)) {
    newErrors.salida =
      "La fecha de salida debe ser posterior a la fecha de entrada";
    formIsValid = false;
  }

  if (!formData.departamento) {
    newErrors.departamento = "El departamento es obligatorio";
    formIsValid = false;
  }

  if (!validarCampoRequerido(formData.nombre || "")) {
    newErrors.nombre = esEmpresa ? "La razón social es obligatoria" : "El nombre es obligatorio";
    formIsValid = false;
  }
  if (apellidoObligatorio(esEmpresa) && !validarCampoRequerido(formData.apellido || "")) {
    newErrors.apellido = "El apellido es obligatorio";
    formIsValid = false;
  }
  
  const esReservaExterna =
    origenReserva === "booking" || origenReserva === "airbnb";
  if (esReservaExterna) {
    if (!validarCampoRequerido((formData.dni || "").trim())) {
      newErrors.dni =
        origenReserva === "booking"
          ? "El código de reserva Booking es obligatorio"
          : "El código de reserva Airbnb es obligatorio";
      formIsValid = false;
    }
  } else if (
    !validarDNIPersonaDirecto(formData.dni || "") &&
    !validarDNI(formData.dni || "")
  ) {
    newErrors.dni = ERROR_DOCUMENTO;
    formIsValid = false;
  }

  if (!validarCantidadHuespedes(formData.cantidad_huespedes || "")) {
    newErrors.cantidad_huespedes =
      "La cantidad de huéspedes debe ser al menos 1";
    formIsValid = false;
  }

  if (!validarValorPositivo(formData.valor_total || "")) {
    newErrors.valor_total = "El valor total debe ser mayor a 0";
    formIsValid = false;
  }

  // Validar que la seña no sea negativa (permitir 0)
  if (formData.pago && !validarValorPositivo(formData.pago)) {
    newErrors.pago = "La seña no puede ser negativa";
    formIsValid = false;
  }

  // 🎯 ELIMINADA: Validación que impedía sobrepago
  // Ahora el sistema de ajuste financiero maneja automáticamente los sobrepagos

  setErrors(newErrors);
  return formIsValid;
};
